<template>
    <header 
        id="header" 
        class="wrapper-header"
        @mouseenter="onHoverHeader()"
    >
        <div class="header" :class="headerClassNames">
            <button
                v-if="isEnabledServiceLink" 
                ref="serviceLink" 
                type="button"
                data-parent="header"
                tabindex="0"
                class="header__freedom-service" 
                @click="onClickService"
                @focus="onFocusService()"
                @blur="onBlurService()"
                > 
                {{ captionFreedomService }}
            </button>
            <div class="header__logo">
                <publisher-icon :icon-name="'IconHome'" />
            </div>
            <div class="header__navigation">
                <nav>
                    <ul
                        id="header-menu"
                        ref="headerMenu"
                        tabindex="0"
                        class="header__menu-items"
                        @focus="onFocus"
                    >
                        <li
                            v-for="menuItem in menuHeader"
                            :key="menuItem.code"
                            class="header-menu__entry"
                        >
                            <a
                                :id="getId(menuItem.code)"
                                :ref="setItemRef"
                                class="header__menu-link"
                                :class="{'header__menu-link_icon': menuItem.icon}"
                                data-type="menu-item"
                                data-parent="header"
                                :data-keyleft="prevItem(menuItem.code)"
                                :data-keyright="nextItem(menuItem.code)"
                                :href="menuItem.routerPath"
                                @focus="onFocusLink()"
                                @blur="onBlurLink()"
                                @click.prevent="goToNextPage(menuItem.routerPath)"
                            >
                                <navigation-icon
                                    v-if="menuItem.icon !== null" 
                                    :icon-name="menuItem.icon" 
                                    class="header__icon"
                                />
                                <span v-else>{{ $t(menuItem.title) }}</span>
                            </a>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    </header>
</template>

<script setup lang="ts">
    import type { LinkItemCode } from "./types/enums/link-item-code";
    import type { LinkItem } from "./types/LinkItem";
    import { linkItems as menuItems } from "./types";
    import { useApplicationStore } from "@/stores/Application";
    import { useDevModeState, type IUseDevModeState } from "@/composables/devMode/useDevModeState"
    import { Strings } from "@/locales/types/enums/strings";
    import { useI18n } from "vue-i18n";
    import { ref, type Ref, computed, onBeforeUnmount, onBeforeUpdate } from "vue";
    import { useRouter, useRoute } from 'vue-router';
    import { storeToRefs } from 'pinia'
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { useUserSettingsStore } from "@/stores/UserSettings";

    const ApplicationStore = useApplicationStore();
    const {
        getHeaderVisibility: isHeaderVisible,
    } = storeToRefs(ApplicationStore);
    const { showHeader, openModalExitApp, deleteAllSelectedIdProgram } = ApplicationStore;

    const UserSettingsStoreModule = useUserSettingsStore();
    const {
        isGuideAvailable,
        isVodAvailable
    } = storeToRefs(UserSettingsStoreModule);
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);


    const router = useRouter();
    const route = useRoute();
    const { $navigator, $eventBus} = useNuxtApp();
    const { t } = useI18n();
    const { linkItems: PublisherLinkItems, isEnabledServiceLinkHeader } = useAppConfig();
    const { activateDevMode }: IUseDevModeState = useDevModeState()
    const captionFreedomService: string = t(Strings.SETTINGS_NAVIGATION_FREEDOM_SERVICE).toString();
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const headerMenu: Ref<HTMLElement | null> = ref(null);
    let menuItemsRefs: Array<Element | ComponentPublicInstance | null> = [];

    //TODO: It is temp solution. We wait for opinion of 'Freedom'
    //const isRegKeyListenerUpForServiceLink: Ref<boolean> = ref(false);

    const isEnabledServiceLink = computed((): boolean => isEnabledServiceLinkHeader && route.path === menuItems.home.routerPath);
    
    const menuHeader = computed((): LinkItem[] =>{
        let res: LinkItem[] = [];
        const menuClient = PublisherLinkItems.main.client;
        const menuGuest = PublisherLinkItems.main.guest;

        if (isGuest.value && menuGuest.length > 0) {
            menuGuest.forEach(item => {
                res.push(menuItems[item]);
            });
        } 

        if (!isGuest.value && menuClient.length > 0) {
            menuClient.forEach(item => {
                res.push(menuItems[item]);
            });
        }

        if (!isGuest.value && res.length > 0) {
            res = res.filter((item: LinkItem)=>{
                if ((item.title == 'menu.guide' && !isGuideAvailable.value) || (item.title == 'menu.onDemand' && !isVodAvailable.value)) {
                    return false;
                } else {
                    return true;
                }
            });
        }
        return res;
    });
    const headerClassNames = computed((): { [key: string]: boolean } => {
        return {
            'header_hidden': !isHeaderVisible.value,
        };
    });
    const currentLinkCode = computed((): LinkItemCode | undefined=>{
        const currentRoute = route.path;
        const currItem: LinkItem | undefined = menuHeader.value.find(({ routerPath }: { routerPath: string }) => {
            if (currentRoute.indexOf(routerPath.toString()) === 0) {
                return true;
            }
            return false
        });
        return currItem?.code;
    });

    const setItemRef = (el: Element | ComponentPublicInstance | null): void => {
        if (el) {
            menuItemsRefs.push(el)
        }
    }

    const onHoverHeader = (): void => {
        showHeader();
        focusItem();
    };
    const onClickService = () => {
        return false; //TODO: It is temp solution. We wait for opinion of 'Freedom'
        //router.push(`${menuItems.settings.routerPath}?openPage=${menuItems.freedomService.code}`);
    };
    const keyBackListener = (): void => {
        openModal();
    };
    const openModal = (): void => {
        openModalExitApp();
    };
    const onKeyDown = (): void => {
        leaveHeader();
    };
    //TODO: It is temp solution. We wait for opinion of 'Freedom'
    /* const  onKeyUp = () => {
        if (serviceLink.value) {
            serviceLink.value.focus();
        }
    }; */
    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            //TODO: It is temp solution. We wait for opinion of 'Freedom'
            /* if (this.isEnabledServiceLink) {
                this.regKeyListenerUpForServiceLink();
            } */
            $navigator.on("keypress:back", keyBackListener);
            $navigator.on("keypress:down", onKeyDown);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            //TODO: It is temp solution. We wait for opinion of 'Freedom'
            /* if (this.isEnabledServiceLink) {
                this.unRegKeyListenerUpForServiceLink();
            } */
            $navigator.off("keypress:back", keyBackListener);
            $navigator.off("keypress:down", onKeyDown);
            isRegKeyListeners.value = false;
        }
    };
    const onFocusService = () => {
        return false; //TODO: It is temp solution. We wait for opinion of 'Freedom'
        //$navigator.on("keypress:down", this.leaveServiceLink);
    };
    const onBlurService = () => {
        return false; //TODO: It is temp solution. We wait for opinion of 'Freedom'
        //$navigator.off("keypress:down", this.leaveServiceLink);
    };
    //TODO: It is temp solution. We wait for opinion of 'Freedom'
    /* const  leaveServiceLink = () => {
        if (headerMenu.value) {
            headerMenu.value.focus();
        }
    }; */
    const onFocusLink = (): void => {
        registerKeyListeners();
    };
    const onBlurLink = (): void => {
        unregisterKeyListeners();
    };
    const goToNextPage = async (url: string): Promise<void> => {
        if (url === menuItems.settings.routerPath) {
            settingsClickHandler();
        }

        if (route.path === url) {
            return;
        }
        deleteAllSelectedIdProgram();
        await router.push(url);
    };
    const prevItem = (menuItemCode: LinkItemCode): string => {
        const ind = menuHeader.value.findIndex(
            (value) => value.code === menuItemCode
        );

        if (ind === 0) {
            return getId(menuHeader.value[menuHeader.value.length - 1].code);
        }

        return getId(menuHeader.value[ind - 1].code);
    };
    const nextItem = (menuItemCode: LinkItemCode): string => {
        const ind = menuHeader.value.findIndex(
            (value) => value.code === menuItemCode
        );

        if (ind === menuHeader.value.length - 1) {
            return getId(menuHeader.value[0].code);
        }

        return getId(menuHeader.value[ind + 1].code);
    };
    const getId = (menuItemCode: LinkItemCode): string => `mi${menuItemCode}`;
    const leaveHeader = (): void => {
        $eventBus.emit('leave-header');
    };
    const focusItem = (): void => {
        if (currentLinkCode.value === undefined) {
            return;
        }

        const activeMenuItemIndex = menuHeader.value.findIndex(
            (value) => value.code === currentLinkCode.value
        );
        if (activeMenuItemIndex === -1) {
            (menuItemsRefs[0] as HTMLElement)?.focus();
            return;
        }
        (menuItemsRefs[activeMenuItemIndex] as HTMLElement)?.focus();
        window.scrollTo(0, 0);
    };
    const onFocus = (): void => {
        showHeader();
        focusItem();
    };
    const settingsClickHandler = (): void => {
        activateDevMode();
    };
    //TODO: It is temp solution. We wait for opinion of 'Freedom'
    /*  const  regKeyListenerUpForServiceLink = () => {
        $navigator.on("keypress:up", onKeyUp);
        isRegKeyListenerUpForServiceLink.value = true;
    };
    const  unRegKeyListenerUpForServiceLink = () => {
        $navigator.off("keypress:up", onKeyUp);
        isRegKeyListenerUpForServiceLink.value = false;
    } */

    onBeforeUpdate(() => {
        menuItemsRefs = []
    })
    onBeforeUnmount(() => {
        unregisterKeyListeners();

        //TODO: It is temp solution. We wait for opinion of 'Freedom'
        /* if (isRegKeyListenerUpForServiceLink.value) {
            unRegKeyListenerUpForServiceLink();
        } */
    });
    //TODO: It is temp solution. We wait for opinion of 'Freedom'
    /* watch(isEnabledServiceLink, (newValue) => {
        if (newValue && !isRegKeyListenerUpForServiceLink.value) {
            regKeyListenerUpForServiceLink();
        } 
        if (!newValue && isRegKeyListenerUpForServiceLink.value) {
            unRegKeyListenerUpForServiceLink();
        }
    }); */
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
